import { styled } from '@openeducation/oe-rn-themes';
import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';

import { OETouchableOpacity } from '../oe-touchable-opacity/oe-touchable-opacity';

export interface ActivityQuestionButtonProps {
  isCorrect: boolean;
  selected: boolean;
  onPress: () => void;
  showResult: boolean;
  children?: React.ReactNode;
}

export function ActivityQuestionButton({
  isCorrect,
  children,
  selected,
  onPress,
  showResult,
}: ActivityQuestionButtonProps) {
  const { current: shakeX } = useRef(new Animated.Value(0));

  useEffect(() => {
    if (!showResult || isCorrect) {
      return;
    }

    Animated.loop(
      Animated.sequence([
        Animated.timing(shakeX, {
          toValue: -5,
          duration: 30,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: 5,
          duration: 60,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: 0,
          duration: 30,
          useNativeDriver: true,
        }),
      ]),
      {
        iterations: 2,
      }
    ).start();
  }, [isCorrect, shakeX, showResult]);

  return (
    <OETouchableOpacity onPress={onPress}>
      <Container
        style={[selected && { transform: [{ translateX: shakeX }] }]}
        selected={selected}
        showResult={showResult}
        isCorrect={isCorrect}
      >
        {children}
      </Container>
    </OETouchableOpacity>
  );
}

interface ContainerProps {
  selected: boolean;
  showResult: boolean;
  isCorrect: boolean;
}

const Container = styled(Animated.View)<ContainerProps>`
  background-color: ${({ isCorrect, selected, showResult, theme }) =>
    selected && showResult && isCorrect
      ? theme.color.middleGreen
      : selected && showResult && !isCorrect
      ? theme.color.middleRed
      : theme.color.white};
  border-color: ${({ theme, selected, isCorrect, showResult }) => {
    if (selected && !showResult) {
      return theme.color.primary;
    }
    if (selected && showResult && isCorrect) {
      return theme.color.middleGreenDark;
    }
    if (selected && showResult && !isCorrect) {
      return theme.color.middleRedDark;
    }
    return theme.color.grayLight;
  }};
  border-width: 3px;
  border-bottom-width: 5px;
  border-radius: 15px;
  padding: 10px;
  margin-bottom: 15px;
  align-items: center;
  justify-content: center;
`;
